import { API_URL } from "./api";

export interface ExportDateRange {
  from?: string;
  to?: string;
}

const withOptionalDateParams = (url: string, range: ExportDateRange): string => {
  const params = new URLSearchParams();

  const from = range.from?.trim();
  const to = range.to?.trim();

  if (from) {
    params.set("from", from);
  }

  if (to) {
    params.set("to", to);
  }

  const query = params.toString();
  return query ? `${url}?${query}` : url;
};

export const buildUsersStatisticsExportUrl = (
  range: ExportDateRange = {}
): string =>
  withOptionalDateParams(
    `${API_URL}/reports/users-statistics/export`,
    range
  );

export const buildReportsLiteExportUrl = (
  range: ExportDateRange = {}
): string => withOptionalDateParams(`${API_URL}/reports/export-lite`, range);

export const buildReportsCleanedExportUrl = (
  range: ExportDateRange = {}
): string => withOptionalDateParams(`${API_URL}/reports/export-cleaned`, range);

const getFilenameFromContentDisposition = (header: string | null): string | null => {
  if (!header) {
    return null;
  }

  const utfMatch = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) {
    return decodeURIComponent(utfMatch[1].replace(/"/g, ""));
  }

  const match = header.match(/filename="?([^";]+)"?/i);
  return match?.[1] || null;
};

export interface DownloadAdminExportOptions {
  url: string;
  token: string;
  fallbackFilename: string;
}

export const downloadAdminExport = async ({
  url,
  token,
  fallbackFilename,
}: DownloadAdminExportOptions): Promise<void> => {
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const message = await response.text().catch(() => "");
    throw new Error(message || `Export failed: HTTP ${response.status}`);
  }

  const blob = await response.blob();
  const filename =
    getFilenameFromContentDisposition(response.headers.get("Content-Disposition")) ||
    fallbackFilename;
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};
